/**
 * CEP Service
 * 
 * Service for fetching address data from CEP (Brazilian postal code)
 * Using multiple APIs for redundancy:
 * - ViaCEP (primary)
 * - BrasilAPI (fallback)
 */

export interface CepData {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge?: string;
  gia?: string;
  ddd?: string;
  siafi?: string;
  erro?: boolean;
}

export interface AddressData {
  cep: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  complement?: string;
}

/**
 * Format CEP to standard format (00000-000)
 */
export function formatCep(cep: string): string {
  const cleaned = cep.replace(/\D/g, '');
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
  }
  return cleaned;
}

/**
 * Clean CEP to numbers only
 */
export function cleanCep(cep: string): string {
  return cep.replace(/\D/g, '');
}

/**
 * Validate CEP format
 */
export function isValidCep(cep: string): boolean {
  const cleaned = cleanCep(cep);
  return cleaned.length === 8 && /^\d{8}$/.test(cleaned);
}

/**
 * Fetch address data from ViaCEP API
 */
async function fetchFromViaCep(cep: string): Promise<CepData | null> {
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep(cep)}/json/`);
    if (!response.ok) return null;
    
    const data: CepData = await response.json();
    if (data.erro) return null;
    
    return data;
  } catch {
    return null;
  }
}

/**
 * Fetch address data from BrasilAPI (fallback)
 */
async function fetchFromBrasilApi(cep: string): Promise<CepData | null> {
  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${cleanCep(cep)}`);
    if (!response.ok) return null;
    
    const data = await response.json();
    
    // Convert BrasilAPI format to ViaCEP format
    return {
      cep: data.cep,
      logradouro: data.street || '',
      complemento: '',
      bairro: data.neighborhood || '',
      localidade: data.city || '',
      uf: data.state || '',
    };
  } catch {
    return null;
  }
}

/**
 * Main function to fetch address by CEP
 * Tries ViaCEP first, then BrasilAPI as fallback
 */
export async function fetchAddressByCep(cep: string): Promise<AddressData | null> {
  if (!isValidCep(cep)) {
    return null;
  }
  
  // Try ViaCEP first
  let data = await fetchFromViaCep(cep);
  
  // If ViaCEP fails, try BrasilAPI
  if (!data) {
    data = await fetchFromBrasilApi(cep);
  }
  
  if (!data) {
    return null;
  }
  
  return {
    cep: formatCep(data.cep),
    street: data.logradouro,
    neighborhood: data.bairro,
    city: data.localidade,
    state: data.uf,
    complement: data.complemento || undefined,
  };
}

export default {
  fetchAddressByCep,
  formatCep,
  cleanCep,
  isValidCep,
};

